import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";

import { UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client-ts";
import { heimdallLocalStorage } from "../tools";
import Loading from "./Loading";
import { isLoginPage } from "../context/auth";
import { FormattedMessage } from "react-intl";
import LoadingScreen from "./LoadingScreen";

const UserManagerContext = createContext(undefined);

async function login(userManager: UserManager): Promise<boolean> {
    const user = await userManager.getUser();
    if (user) {
        console.debug("refresh token available");
        return true;
    }

    const loc = window.location;

    const path = `${loc.protocol}//${loc.host}${loc.pathname}`;
    console.debug(`Handle login on ${path}`);

    if (path === window.oidcClientSettings.redirect_uri) {
        console.debug("Processing PCKE response");
        await userManager.signinCallback(window.location.href);

        console.debug("Login successful");
        window.location.href = heimdallLocalStorage.beforeLoginUrl;
    } else {
        console.debug("Start PKCE sign-in");
        heimdallLocalStorage.beforeLoginUrl = window.location.href;
        await userManager.signinRedirect();
    }

    return false;
}

export function useUserManager(): UserManager {
    return useContext(UserManagerContext);
}

function finalClientSettings(): UserManagerSettings {
    return { ...window.oidcClientSettings, userStore: new WebStorageStateStore({ store: window.localStorage }) };
}

/**
 * Ensures that the user is logged in and the profile is available. Loading screen in displayed until then.
 *
 * The validity of the access token is not checked. It will be automatically refreshed by other code and if the refresh
 * fails, then the reload get us back here to refresh the login.
 */
export default function EnsureLogin({ children }: { children: ReactNode }) {
    const [ready, setReady] = useState(false);
    const userManager = useMemo(() => new UserManager(finalClientSettings()), []);
    // const dispatch = useDispatch();
    //
    if (isLoginPage(window.location.pathname)) {
        console.debug("EnsureLogin fallthrough on login page");
        return <UserManagerContext.Provider value={userManager}>{children}</UserManagerContext.Provider>;
    } else console.debug(`EnsureLogin no fallthrough on "${window.location.pathname}"`);

    useEffect(() => {
        login(userManager)
            .then((ready) => {
                setReady(ready);
            })
            .catch((exc) => {
                console.info(`PKCE process failed: ${exc}`);
                // dispatch(setBackendError({code: "cant_reach_heimdall"}));
            });
    }, [setReady]);

    if (!ready) return <LoadingScreen />;

    return <UserManagerContext.Provider value={userManager}>{children}</UserManagerContext.Provider>;
}
